"use client";

import { c as _c } from "react/compiler-runtime";
import { Carousel, type CarouselApi, CarouselContent, CarouselItem } from "@repo/design-system/components/ui/carousel";
import { CustomImage } from "@repo/design-system/components/ui/custom-image";
import { cn } from "@repo/design-system/utils/cn";
import Autoplay from "embla-carousel-autoplay";
import { useEffect, useMemo, useRef, useState } from "react";
import YouTubePlayer from "~/components/job-page/jobadvideo/youtube-eon";
import { useLanguage } from "~/translations/loader/use-language";
interface Report {
  cardIndex: number;
  image: string;
  text: string;
  title: string;
  videoId: string;
  videoTitle: string;
}
interface CardProps extends Report {
  isActive: boolean;
}
function Card(t0) {
  const $ = _c(21);
  const {
    title,
    text,
    image,
    isActive
  } = t0;
  const t1 = isActive && "scale-105";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("relative m-3 mt-10 aspect-square cursor-pointer transition-transform duration-300 hover:scale-105", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== image || $[3] !== title) {
    t3 = <div className="relative"><CustomImage alt={title} className="object-cover" fill={true} sizes="(min-width: 1280px) 427px, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={image} /></div>;
    $[2] = image;
    $[3] = title;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== title) {
    t4 = <h3 className="font-bold text-xl">{title}</h3>;
    $[5] = title;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== text) {
    t5 = <p className="mt-2 text-lg">{text}</p>;
    $[7] = text;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className="bg-[#f7e8da] p-2 text-foreground md:p-6">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3 || $[13] !== t6) {
    t7 = <div className="grid h-full w-full auto-rows-[3fr_2fr] overflow-hidden rounded-3xl">{t3}{t6}</div>;
    $[12] = t3;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== isActive) {
    t8 = isActive && <div className="absolute top-full right-1/2 mx-auto hidden h-0 w-0 translate-x-1/2 border-[10px] border-transparent border-t-[#f7e8da] border-t-[12px] border-b-0 md:block" />;
    $[15] = isActive;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t2 || $[18] !== t7 || $[19] !== t8) {
    t9 = <div className={t2}>{t7}{t8}</div>;
    $[17] = t2;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}
export default function ExperienceReportsEcon() {
  const [api, setApi] = useState<CarouselApi>();
  const [activeCard, setActiveCard] = useState(0);
  const [videoStarted, setVideoStarted] = useState(false);
  const autoplayRef = useRef(Autoplay({
    delay: 5000,
    stopOnInteraction: true
  }));
  const t = useLanguage();
  const cards: Report[] = useMemo(() => [{
    cardIndex: 0,
    title: t.econ_exp_card1_title,
    text: t.econ_exp_card1_text,
    image: "images/jobad/econ/experiencereports/exp_report1.webp",
    videoId: "z0l9xljlQyo",
    videoTitle: t.econ_exp_card1_videoTitle
  }, {
    cardIndex: 1,
    title: t.econ_exp_card2_title,
    text: t.econ_exp_card2_text,
    image: "images/jobad/econ/experiencereports/exp_report2.webp",
    videoId: "4aW1UTmkdHM",
    videoTitle: t.econ_exp_card2_videoTitle
  }, {
    cardIndex: 2,
    title: t.econ_exp_card3_title,
    text: t.econ_exp_card3_text,
    image: "images/jobad/econ/experiencereports/exp_report3.webp",
    videoId: "QOgaOtexsPM",
    videoTitle: t.econ_exp_card3_videoTitle
  }], [t]);

  // Track current slide index
  useEffect(() => {
    if (!api) {
      return;
    }
    const updateIndex = () => setActiveCard(api.selectedScrollSnap());
    updateIndex();
    api.on("select", updateIndex);
    return () => {
      api.off("select", updateIndex);
    };
  }, [api]);

  // Stop autoplay when video starts
  useEffect(() => {
    if (videoStarted) {
      autoplayRef.current.stop();
    }
  }, [videoStarted]);
  return <section className="outer-container bg-gradient-to-br from-[#ea1b0a] to-[#00375c] py-10 text-white md:py-20 print:hidden" id="experienceReports">
      <div className="inner-container">
        <h2 className="mb-4 text-center font-bold text-4xl md:mb-6 xl:text-5xl">
          {t.econ_exp_HL}
        </h2>
        <h3 className="mb-4 text-center text-xl md:mb-6 lg:text-2xl">
          {t.econ_exp_subHL}
        </h3>

        {/* Mobile/Tablet: Carousel */}
        <Carousel className="w-full lg:hidden" opts={{
        loop: true,
        align: "center"
      }} plugins={[autoplayRef.current]} setApi={setApi}>
          <CarouselContent className="-ml-2 md:-ml-4">
            {cards.map(card => <CarouselItem className="basis-full pl-2 sm:basis-1/2 md:pl-4" key={card.cardIndex}>
                <button className="w-full" onClick={() => api?.scrollTo(card.cardIndex)} type="button">
                  <Card {...card} isActive={activeCard === card.cardIndex} />
                </button>
              </CarouselItem>)}
          </CarouselContent>
        </Carousel>

        {/* Desktop: Grid layout */}
        <div className="mb-8 hidden auto-cols-fr grid-flow-col lg:grid">
          {cards.map(card_0 => <button key={card_0.cardIndex} onClick={() => setActiveCard(card_0.cardIndex)} type="button">
              <Card {...card_0} isActive={activeCard === card_0.cardIndex} />
            </button>)}
        </div>

        <div className="mt-8 w-full bg-white p-4 lg:flex lg:p-8">
          <div className="lg:w-1/3">
            <h3 className="mb-8 font-bold text-3xl text-black lg:w-1/2 lg:text-5xl">
              {cards[activeCard]?.videoTitle}
            </h3>
          </div>
          <div className="flex w-full lg:mr-auto lg:justify-end">
            <YouTubePlayer onVideoStart={() => setVideoStarted(true)} thumbnailSrc={`https://img.youtube.com/vi/${cards[activeCard]?.videoId}/hqdefault.jpg`} videoId={cards[activeCard]?.videoId || ""} />
          </div>
        </div>
      </div>
    </section>;
}